import React, { useState, useEffect, useCallback } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import TechnicianPerformanceTable from './components/TechnicianPerformanceTable';
import FilterPanel from './components/FilterPanel';
import RealtimeMonitor from './components/RealtimeMonitor';
import GeographicDistribution from './components/GeographicDistribution';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { APP_NAME } from '../../constants';
import { supabase } from '../../lib/supabase';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('last30days');
  
  // State pour les données
  const [technicianData, setTechnicianData] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [performanceChartData, setPerformanceChartData] = useState([]);
  const [realtimeData, setRealtimeData] = useState(null);
  const [geographicData, setGeographicData] = useState([]);

  // Récupérer les techniciens depuis la table `user_profiles` (role: technician)
  const fetchTechnicians = useCallback(async () => {
    try {
      const { data: technicians, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, specialty, avatar_url, is_active, rating, created_at')
        .eq('role', 'technicien');

      if (error) throw error;

      if (technicians && technicians.length > 0) {
        // Récupérer les appointments par technicien
        const { data: appointments, error: appError } = await supabase
          .from('appointments')
          .select('id, technician, status, scheduled_date, completion_time, price_total')
         

        if (appError) console.warn('Erreur appointments:', appError);

        const formattedTechs = technicians.map(tech => {
          // Note: la colonne dans appointments s'appelle 'technician' (référence vers user_profiles.id)
          const techAppointments = appointments?.filter(a => a.technician === tech.id) || [];
          const completedAppointments = techAppointments.filter(a => a.status === 'completed');
          
          const totalDuration = completedAppointments.reduce((sum, a) => {
            if (a.scheduled_date && a.completion_time) {
              const start = new Date(a.scheduled_date);
              const end = new Date(a.completion_time);
              const minutes = (end - start) / (1000 * 60);
              return sum + (minutes > 0 ? minutes : 0);
            }
            return sum;
          }, 0);
          
          const avgDuration = completedAppointments.length > 0 ? totalDuration / completedAppointments.length : 0;
          const avgSatisfaction = tech.rating || 0;
          const completionRate = techAppointments.length > 0 
            ? Math.round((completedAppointments.length / techAppointments.length) * 100) 
            : 0;
            
          return {
            id: tech.id,
            name: tech.full_name,
            specialty: tech.specialty || 'Généraliste',
            avatar: tech.avatar_url || '',
            avatarAlt: `Portrait de ${tech.full_name}`,
            interventions: techAppointments.length,
            completionRate: completionRate,
            avgTime: `${Math.floor(avgDuration / 60)}h ${Math.floor(avgDuration % 60)}min`,
            satisfaction: parseFloat(avgSatisfaction.toFixed(1)),
            status: tech.is_active ? 'active' : 'offline'
          };
        });
        
        setTechnicianData(formattedTechs);
      } else {
        setTechnicianData([]);
      }
    } catch (error) {
      console.error('Erreur fetchTechnicians:', error);
      setTechnicianData([]);
    }
  }, []);

  // Récupérer les métriques KPI depuis `appointments`
  const fetchKpiMetrics = useCallback(async () => {
    try {
      const dateFilter = new Date();
      switch(dateRange) {
        case 'last7days': dateFilter.setDate(dateFilter.getDate() - 7); break;
        case 'last30days': dateFilter.setDate(dateFilter.getDate() - 30); break;
        case 'thisMonth': dateFilter.setDate(1); break;
        case 'last6months': dateFilter.setMonth(dateFilter.getMonth() - 6); break;
        default: dateFilter.setDate(dateFilter.getDate() - 30);
      }

      const { data: appointments, error } = await supabase
        .from('appointments')
        .select('id, status, scheduled_date, completion_time, price_total')
        .gte('scheduled_date', dateFilter.toISOString().split('T')[0]);

      if (error) throw error;

      if (appointments && appointments.length > 0) {
        const total = appointments.length;
        const completed = appointments.filter(a => a.status === 'completed').length;
        const cancelled = appointments.filter(a => a.status === 'cancelled').length;
        
        // Temps moyen d'intervention
        let avgDuration = 0;
        const withDuration = appointments.filter(a => a.completion_time && a.scheduled_date);
        if (withDuration.length > 0) {
          const totalMinutes = withDuration.reduce((sum, a) => {
            const start = new Date(a.scheduled_date);
            const end = new Date(a.completion_time);
            const minutes = (end - start) / (1000 * 60);
            return sum + (minutes > 0 ? minutes : 0);
          }, 0);
          avgDuration = totalMinutes / withDuration.length;
        }
        
        // Chiffre d'affaires total
        const totalRevenue = appointments.reduce((sum, a) => sum + (a.price_total || 0), 0);
        
        // Taux de conversion
        const conversionRate = total > 0 ? (completed / total) * 100 : 0;
        
        // Taux d'annulation
        const cancellationRate = total > 0 ? (cancelled / total) * 100 : 0;

        setKpiMetrics([
          { title: 'Taux de conversion', value: `${conversionRate.toFixed(1)}%`, change: '+5,2%', changeType: 'positive', icon: 'TrendingUp', trend: [45, 52, 48, 61, 55, 68, 72, 68] },
          { title: "Taux d'annulation", value: `${cancellationRate.toFixed(1)}%`, change: '-1,8%', changeType: 'positive', icon: 'UserX', trend: [8, 7, 6, 5, 6, 5, 4, 4] },
          { title: 'Temps moyen intervention', value: `${Math.floor(avgDuration / 60)}h ${Math.floor(avgDuration % 60)}min`, change: '-12min', changeType: 'positive', icon: 'Clock', trend: [150, 145, 140, 138, 135, 135, 132, 135] },
          { title: 'Chiffre d\'affaires', value: `${totalRevenue.toLocaleString()} €`, change: '+8,3%', changeType: 'positive', icon: 'Euro', trend: [12500, 15200, 14800, 18900, 16700, 21300, 19800] }
        ]);
      } else {
        setKpiMetrics([]);
      }
    } catch (error) {
      console.error('Erreur fetchKpiMetrics:', error);
      setKpiMetrics([]);
    }
  }, [dateRange]);

  // Récupérer les données temps réel
  const fetchRealtimeMetrics = useCallback(async () => {
    try {
      const { data: activeAppointments, error } = await supabase
        .from('appointments')
        .select('id, technician')
        .in('status', ['planned', 'completed', 'on_site']);

      if (error) throw error;

      const { data: activeTechnicians, error: techError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('role', 'technicien')
        .eq('is_active', true);

      if (techError) throw techError;

      const totalTechnicians = activeTechnicians?.length || 1;
      const occupancyRate = totalTechnicians > 0 
        ? Math.min(100, Math.round(((activeAppointments?.length || 0) / totalTechnicians) * 100))
        : 0;

      setRealtimeData({
        lastUpdate: new Date().toLocaleTimeString(),
        metrics: [
          { id: 1, label: 'Interventions actives', description: 'En cours actuellement', value: (activeAppointments?.length || 0).toString(), change: '+3', changeType: 'positive', status: 'active' },
          { id: 2, label: 'Techniciens disponibles', description: 'Prêts à intervenir', value: (activeTechnicians?.length || 0).toString(), change: '+2', changeType: 'positive', status: 'active' },
          { id: 3, label: "Taux d'occupation", description: 'Techniciens en intervention', value: `${occupancyRate}%`, change: '+5%', changeType: 'positive', status: 'warning' }
        ]
      });
    } catch (error) {
      console.error('Erreur fetchRealtimeMetrics:', error);
      setRealtimeData(null);
    }
  }, []);

  // Récupérer les données géographiques
  const fetchGeographicData = useCallback(async () => {
    try {
      const { data: appointments, error } = await supabase
        .from('appointments')
        .select('id, customer_address, customer_latitude, customer_longitude, price_total, status')
        .eq('status', 'completed');

      if (error) throw error;

      if (appointments && appointments.length > 0) {
        const locationMap = new Map();
        
        appointments.forEach(app => {
          if (app.customer_latitude && app.customer_longitude) {
            const latZone = Math.round(app.customer_latitude * 10) / 10;
            const lonZone = Math.round(app.customer_longitude * 10) / 10;
            const zoneKey = `${latZone},${lonZone}`;
            
            if (!locationMap.has(zoneKey)) {
              locationMap.set(zoneKey, {
                interventions: 0,
                revenue: 0,
                name: `Zone ${locationMap.size + 1}`
              });
            }
            
            const zone = locationMap.get(zoneKey);
            zone.interventions++;
            zone.revenue += app.price_total || 0;
          }
        });
        
        const totalInterventions = appointments.length;
        const geoData = Array.from(locationMap.entries()).map(([key, data], index) => ({
          id: index + 1,
          name: data.name,
          interventions: data.interventions,
          percentage: totalInterventions > 0 ? Math.round((data.interventions / totalInterventions) * 100) : 0,
          revenue: `${Math.round(data.revenue).toLocaleString()} €`,
          satisfaction: 0,
          technicians: Math.max(1, Math.ceil(data.interventions / 50))
        }));
        
        setGeographicData(geoData.slice(0, 5));
      } else {
        setGeographicData([]);
      }
    } catch (error) {
      console.error('Erreur fetchGeographicData:', error);
      setGeographicData([]);
    }
  }, []);

  // Récupérer les données pour les graphiques
  const fetchChartData = useCallback(async () => {
    try {
      // Données pour le graphique des revenus (7 derniers jours)
      const last7Days = new Date();
      last7Days.setDate(last7Days.getDate() - 7);
      
      const { data: weeklyData, error: weeklyError } = await supabase
        .from('appointments')
        .select('scheduled_date, price_total')
        .eq('status', 'completed')
        .gte('scheduled_date', last7Days.toISOString().split('T')[0]);

      if (weeklyError) throw weeklyError;

      if (weeklyData && weeklyData.length > 0) {
        const daysMap = { Lun: 0, Mar: 0, Mer: 0, Jeu: 0, Ven: 0, Sam: 0, Dim: 0 };
        const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        
        weeklyData.forEach(app => {
          if (app.scheduled_date) {
            const date = new Date(app.scheduled_date);
            const dayName = days[date.getDay()];
            if (daysMap[dayName] !== undefined) {
              daysMap[dayName] += app.price_total || 0;
            }
          }
        });
        
        const chartData = Object.entries(daysMap).map(([name, revenus]) => ({
          name,
          revenus,
          interventions: Math.floor(revenus / 200)
        }));
        
        setRevenueChartData(chartData);
      } else {
        setRevenueChartData([]);
      }

      // Données pour les performances mensuelles
      const last6Months = new Date();
      last6Months.setMonth(last6Months.getMonth() - 6);
      
      const { data: monthlyData, error: monthlyError } = await supabase
        .from('appointments')
        .select('scheduled_date, status')
        .gte('scheduled_date', last6Months.toISOString().split('T')[0]);

      if (monthlyError) throw monthlyError;

      if (monthlyData && monthlyData.length > 0) {
        const monthsMap = {};
        const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        
        monthlyData.forEach(app => {
          if (app.scheduled_date) {
            const date = new Date(app.scheduled_date);
            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            const monthName = monthNames[date.getMonth()];
            
            if (!monthsMap[monthKey]) {
              monthsMap[monthKey] = { name: monthName, total: 0, completed: 0 };
            }
            monthsMap[monthKey].total++;
            if (app.status === 'completed') {
              monthsMap[monthKey].completed++;
            }
          }
        });
        
        const perfData = Object.values(monthsMap)
          .slice(-6)
          .map(month => ({
            name: month.name,
            completionRate: month.total > 0 ? Math.round((month.completed / month.total) * 100) : 0,
            satisfaction: 0,
            etaAccuracy: 0
          }));
        
        setPerformanceChartData(perfData);
      } else {
        setPerformanceChartData([]);
      }
    } catch (error) {
      console.error('Erreur fetchChartData:', error);
      setRevenueChartData([]);
      setPerformanceChartData([]);
    }
  }, []);

  // Chargement principal de toutes les données
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTechnicians(),
        fetchKpiMetrics(),
        fetchRealtimeMetrics(),
        fetchGeographicData(),
        fetchChartData()
      ]);
    } catch (error) {
      console.error('Erreur chargement global:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchTechnicians, fetchKpiMetrics, fetchRealtimeMetrics, fetchGeographicData, fetchChartData]);

  // Effets pour charger les données
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (dateRange) {
      fetchAllData();
    }
  }, [dateRange, fetchAllData]);

  const handleLogout = () => console.log('Déconnexion');
  const handleApplyFilters = (filters) => { if (filters?.dateRange) setDateRange(filters.dateRange); };
  const handleExportReport = () => console.log('Export du rapport');

  const savedPresets = [
    { id: 1, name: 'Rapport mensuel', filters: { dateRange: 'thisMonth', serviceType: 'all', technicianTeam: 'all', region: 'all' } },
    { id: 2, name: 'Performance urgences', filters: { dateRange: 'last7days', serviceType: 'emergency', technicianTeam: 'all', region: 'all' } },
    { id: 3, name: 'Analyse Paris', filters: { dateRange: 'last30days', serviceType: 'all', technicianTeam: 'all', region: 'paris' } }
  ];

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
    { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
    { id: 'geographic', label: 'Géographie', icon: 'Map' },
    { id: 'realtime', label: 'Temps réel', icon: 'Activity' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole="admin" userName={`Admin ${APP_NAME}`} onLogout={handleLogout} />
      <main className="main-content">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">Tableau de bord analytique</h1>
                <p className="text-muted-foreground">Surveillance des performances et indicateurs clés</p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" iconName="Download" onClick={handleExportReport}>Exporter le rapport</Button>
                <Button variant="default" iconName="Settings">Configurer</Button>
              </div>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${activeTab === tab.id ? 'bg-primary text-primary-foreground shadow-md' : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground border border-border'}`}>
                  <Icon name={tab.icon} size={18} /><span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <FilterPanel onApplyFilters={handleApplyFilters} savedPresets={savedPresets} />

          {activeTab === 'overview' && (
            <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiMetrics.slice(0, 8).map((metric, index) => (
                  <MetricCard key={index} title={metric.title} value={metric.value} change={metric.change} changeType={metric.changeType} icon={metric.icon} trend={metric.trend} loading={loading} />
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PerformanceChart title="Revenus hebdomadaires" data={revenueChartData} type="bar" dataKeys={[{ key: 'revenus', name: 'Revenus (€)', color: '#2563EB' }, { key: 'interventions', name: 'Interventions', color: '#10B981' }]} loading={loading} />
                <PerformanceChart title="Évolution des performances" data={performanceChartData} type="line" dataKeys={[{ key: 'completionRate', name: 'Taux de résolution (%)', color: '#2563EB' }]} loading={loading} />
              </div>
              <TechnicianPerformanceTable data={technicianData} loading={loading} />
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {kpiMetrics.slice(0, 6).map((metric, index) => (
                  <MetricCard key={index} title={metric.title} value={metric.value} change={metric.change} changeType={metric.changeType} icon={metric.icon} trend={metric.trend} loading={loading} />
                ))}
              </div>
              <TechnicianPerformanceTable data={technicianData} loading={loading} />
              <PerformanceChart title="Analyse détaillée mensuelle" data={performanceChartData} type="line" height={400} dataKeys={[{ key: 'completionRate', name: 'Taux de résolution (%)', color: '#2563EB' }]} loading={loading} />
            </div>
          )}

          {activeTab === 'geographic' && (
            <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard title="Zones couvertes" value={geographicData.length.toString()} icon="MapPin" loading={loading} change="" changeType="neutral" />
                <MetricCard title="Interventions totales" value={geographicData.reduce((s, d) => s + d.interventions, 0).toLocaleString()} change="+12,5%" changeType="positive" icon="Activity" loading={loading} />
                <MetricCard title="Chiffre d'affaires" value={geographicData.reduce((s, d) => s + parseInt(d.revenue.replace(/[^0-9]/g, '0')), 0).toLocaleString() + ' €'} change="+8,3%" changeType="positive" icon="Euro" loading={loading} />
              </div>
              <GeographicDistribution data={geographicData} loading={loading} />
            </div>
          )}

          {activeTab === 'realtime' && (
            <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <RealtimeMonitor data={realtimeData || { lastUpdate: '', metrics: [] }} loading={loading} />
                </div>
                <div className="space-y-4">
                  <MetricCard title="Techniciens actifs" value={`${technicianData.filter(t => t.status === 'active').length}/${technicianData.length}`} icon="Users" loading={loading} change="" changeType="neutral" />
                  <MetricCard title="Note moyenne" value={`${(technicianData.reduce((s, t) => s + (t.satisfaction || 0), 0) / (technicianData.length || 1)).toFixed(1)}/5`} icon="Star" loading={loading} change="+0,2" changeType="positive" />
                  <MetricCard title="Disponibilité" value="98,5%" icon="CheckCircle" loading={loading} change="" changeType="neutral" />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PerformanceChart title="Activité temps réel" data={revenueChartData} type="line" dataKeys={[{ key: 'interventions', name: 'Interventions', color: '#2563EB' }]} loading={loading} />
                <TechnicianPerformanceTable data={technicianData.slice(0, 5)} loading={loading} />
              </div>
            </div>
          )}
        </div>
      </main>
      <QuickActionToolbar userRole="admin" onEmergencyCall={() => console.log('Urgence')} onQuickBooking={() => console.log('Réservation rapide')} onSupportChat={() => console.log('Support')} />
    </div>
  );
};

export default AnalyticsDashboard;